import { DateTime } from "luxon";
import { type DateRange, type DateRangePresetKey } from "./types";

/**
 * A relative-range preset: a stable key, the menu label, and a pure resolver
 * that maps a reference instant to a concrete {@link DateRange}.
 */
export type DateRangePreset = {
  key: DateRangePresetKey;
  label: string;
  /** Resolve the range relative to `now`, in `now`'s timezone. */
  resolve: (now: DateTime) => DateRange;
};

/**
 * Presets are **rolling windows** anchored on today: `to` is the end of today
 * and `from` counts back from the start of today — a fixed number of days for
 * Week/Month, or the calendar-aligned month/year offset for Quarter/Year. All
 * math is done in the reference instant's timezone (the component passes the
 * user's local time), so ranges follow the viewer's calendar.
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: "today",
    label: "Today",
    resolve: (now) => ({
      from: now.startOf("day").toJSDate(),
      to: now.endOf("day").toJSDate(),
    }),
  },
  {
    key: "pastWeek",
    label: "Past Week",
    resolve: (now) => ({
      from: now.startOf("day").minus({ days: 7 }).toJSDate(),
      to: now.endOf("day").toJSDate(),
    }),
  },
  {
    key: "pastMonth",
    label: "Past Month",
    resolve: (now) => ({
      from: now.startOf("day").minus({ days: 30 }).toJSDate(),
      to: now.endOf("day").toJSDate(),
    }),
  },
  {
    key: "pastQuarter",
    label: "Past Quarter",
    resolve: (now) => ({
      from: now.startOf("day").minus({ months: 3 }).toJSDate(),
      to: now.endOf("day").toJSDate(),
    }),
  },
  {
    key: "pastYear",
    label: "Past Year",
    resolve: (now) => ({
      from: now.startOf("day").minus({ years: 1 }).toJSDate(),
      to: now.endOf("day").toJSDate(),
    }),
  },
];

/** The Today preset — the canonical default selection. */
export const TODAY_PRESET: DateRangePreset =
  DATE_RANGE_PRESETS.find((preset) => preset.key === "today") ?? DATE_RANGE_PRESETS[0];

/**
 * Return the preset key whose resolved range (relative to `now`) exactly
 * matches `value`, or `null` when the range is custom. Drives the trigger
 * label and the highlighted menu row.
 */
export const matchPreset = (value: DateRange, now: DateTime): DateRangePresetKey | null => {
  const match = DATE_RANGE_PRESETS.find((preset) => {
    const resolved = preset.resolve(now);
    return (
      resolved.from.getTime() === value.from.getTime() &&
      resolved.to.getTime() === value.to.getTime()
    );
  });
  return match?.key ?? null;
};
